import { Button, Chip, Paper, Typography } from '@material-ui/core';
import type {
  ManagementTimelineGroup,
  ManagementTimelineMarker,
} from '@internal/spassu-code-contract';
import type { CockpitViewModel } from '../model/cockpitViewModel';
import { formatCockpitDateTime } from '../model/formatters';
import {
  timelineOutcomeLabel,
  timelineTypeLabel,
} from '../model/semanticLabels';
import { TimelineMarkerDetail } from './TimelineMarkerDetail';
import { useCockpitStyles } from './cockpitStyles';

export const TimelineMarkerCard = ({
  marker,
  model,
  group,
  expanded,
  selected,
  onExpandGroup,
  onSelect,
}: {
  marker: ManagementTimelineMarker;
  model: CockpitViewModel;
  group?: ManagementTimelineGroup;
  expanded: boolean;
  selected: boolean;
  onExpandGroup(groupKey: string): void;
  onSelect(markerId: string): void;
}) => {
  const classes = useCockpitStyles();
  const cardClass = `${classes.timelineCard} ${
    marker.attentionRequired ? classes.timelineCritical : ''
  } ${selected ? classes.timelineSelected : ''}`;
  return (
    <li>
      <Paper className={cardClass} variant="outlined">
        <TimelineMarkerSummary marker={marker} />
        {group && !expanded && (
          <TimelineGroupSummary group={group} onExpand={onExpandGroup} />
        )}
        <TimelineTechnicalDetails marker={marker} />
        <div>
          <Button
            size="small"
            aria-expanded={selected}
            onClick={() => onSelect(marker.id)}
          >
            {selected ? 'Fechar detalhes de' : 'Ver detalhes de'} {marker.title}
          </Button>
        </div>
        {selected && <TimelineMarkerDetail marker={marker} model={model} />}
      </Paper>
    </li>
  );
};

function TimelineMarkerSummary({
  marker,
}: {
  marker: ManagementTimelineMarker;
}) {
  const classes = useCockpitStyles();
  return (
    <>
      <div className={classes.timelineMeta}>
        <Typography variant="h6">{marker.title}</Typography>
        {marker.attentionRequired && (
          <Chip size="small" color="secondary" label="Requer atenção" />
        )}
      </div>
      <Typography>{marker.summary}</Typography>
      <Typography variant="body2" color="textSecondary">
        {timelineTypeLabel(marker.type)} ·{' '}
        {timelineOutcomeLabel(marker.outcome)} ·{' '}
        {formatCockpitDateTime(marker.timestamp)}
      </Typography>
      {marker.skillName && (
        <Typography variant="body2">Skill: {marker.skillName}</Typography>
      )}
    </>
  );
}

function TimelineGroupSummary({
  group,
  onExpand,
}: {
  group: ManagementTimelineGroup;
  onExpand(groupKey: string): void;
}) {
  const classes = useCockpitStyles();
  return (
    <div className={classes.timelineGroup}>
      <Typography variant="body2">
        {group.count} ocorrências entre {formatGroupDate(group.firstTimestamp)}{' '}
        e {formatGroupDate(group.lastTimestamp)}.
      </Typography>
      <Button size="small" onClick={() => onExpand(group.key)}>
        Consultar ocorrências
      </Button>
    </div>
  );
}

function TimelineTechnicalDetails({
  marker,
}: {
  marker: ManagementTimelineMarker;
}) {
  const classes = useCockpitStyles();
  if (marker.technicalEvents.length === 0) return null;
  return (
    <details className={classes.progressiveDetails}>
      <summary>Dados técnicos ({marker.technicalEvents.length})</summary>
      {marker.technicalEvents.map(event => (
        <Typography key={event.id} variant="body2">
          {formatCockpitDateTime(event.timestamp)} · {event.summary}
        </Typography>
      ))}
    </details>
  );
}

function formatGroupDate(value?: string): string {
  return value ? formatCockpitDateTime(value) : 'período não informado';
}
